import styles from './style.module.scss';
import { useGlobalStore } from 'shared/store';
import { formatCompactNumber } from 'shared/utils';
import { useNavigate } from 'react-router-dom';
import { ROUTES, TOKEN_ICONS } from 'shared/constants';
import { useInitData } from '@vkruglikov/react-telegram-web-app';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UIButton } from 'shared/ui';
import { WithdrawAmountModal } from 'components/modals/withdraw-amount-modal/withdraw-amount-modal';
import { WithdrawSuccessModal } from 'components/modals/withdraw-success-modal/withdraw-success-modal';
import { withdrawUSDT } from 'shared/api/queries/withdrawal';
import leftRect2 from 'assets/header/left-rect-2.svg';
import leftRect1 from 'assets/header/left-rect-1.svg';
import rightRect1 from 'assets/header/right-rect-1.svg';
import rightRect2 from 'assets/header/right-rect-2.svg';
import plusBuyImage from 'assets/images/plus-buy.png';

export const AppHeader = () => {
  const user = useGlobalStore((state) => state.user);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [initDataUnsafe] = useInitData();
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawResult, setWithdrawResult] = useState<{
    amount?: number;
    transactionId?: string;
    error?: string;
  }>({});

  const avatar = window.Telegram?.WebApp.initDataUnsafe.user?.photo_url;
  const displayName =
    initDataUnsafe?.user?.username ||
    initDataUnsafe?.user?.first_name ||
    initDataUnsafe?.user?.last_name ||
    'BoBe';

  const handleWithdrawClick = () => {
    setWithdrawAmount('');
    setShowWithdrawModal(true);
  };

  const handleWithdrawComplete = async (amount: number, walletAddress: string) => {
    try {
      const response = await withdrawUSDT({
        amount,
        wallet: walletAddress,
      });

      if (response.success) {
        // Update user balance if backend returns updated user
        if (response.user) {
          useGlobalStore.getState().updateUser(response.user);
        }

        setWithdrawResult({
          amount,
          transactionId: response.transaction_id,
        });
        setShowWithdrawModal(false);
        setShowSuccessModal(true);
      } else {
        throw new Error(response.message || 'Withdrawal failed');
      }
    } catch (error) {
      setWithdrawResult({
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      });
      setShowWithdrawModal(false);
      setShowSuccessModal(true);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setWithdrawResult({});
  };

  return (
    <div className={styles.header}>
      {/* Outer left - DON */}
      <div className={`${styles.balanceContainer} ${styles.leftOuter} ${styles.outerSize}`}>
        <img src={leftRect2} alt="" className={styles.bgSvg} />
        <div className={`${styles.contentColumn} ${styles.rotateLeft}`}>
          <img src={TOKEN_ICONS.donCoin} alt="" className={`${styles.icon} ${styles.glowPink}`} />
          <span className={styles.value}>{formatCompactNumber(Number(user.coins))}</span>
        </div>
      </div>

      {/* Inner left - USDT */}
      <div className={`${styles.balanceContainer} ${styles.leftInner}`}>
        <img src={leftRect1} alt="" className={styles.bgSvg} />
        <div className={styles.content}>
          <img src={TOKEN_ICONS.usdt} alt="" className={`${styles.icon} ${styles.glowGreen}`} />
          <span className={styles.value}>{formatCompactNumber(Number(user.usdt))}</span>
        </div>
        {Number(user.usdt) >= 10 && (
          <UIButton
            variant="primary"
            className={styles.withdrawButton}
            onClick={handleWithdrawClick}
          >
            {t('v2.withdrawal.withdraw')}
          </UIButton>
        )}
      </div>

      {/* Inner right - BoBe */}
      <div className={`${styles.balanceContainer} ${styles.rightInner}`}>
        <img src={rightRect1} alt="" className={styles.bgSvg} />
        <div className={styles.content}>
          <img
            src={TOKEN_ICONS.bobeCoin}
            alt=""
            className={`${styles.icon} ${styles.glowYellow}`}
          />
          <div
            className={styles.valueWithBuy}
            onClick={() => navigate(ROUTES.MARKET, { state: { category: 'chests' } })}
          >
            <span className={styles.value}>{formatCompactNumber(Number(user.tokens))}</span>
            <img src={plusBuyImage} alt="Buy" className={styles.icon} />
          </div>
        </div>
      </div>

      {/* Outer right - Energy */}
      <div className={`${styles.balanceContainer} ${styles.rightOuter} ${styles.outerSize}`}>
        <img src={rightRect2} alt="" className={styles.bgSvg} />
        <div className={`${styles.contentColumn} ${styles.rotateRight}`}>
          <img
            src={TOKEN_ICONS.energyCharge}
            alt=""
            className={`${styles.icon} ${styles.glowBlue}`}
          />
          <div
            className={styles.valueWithBuy}
            onClick={() => navigate(ROUTES.MARKET, { state: { category: 'bots' } })}
          >
            <span className={styles.value}>{formatCompactNumber(user.energy_charges)}</span>
            <img src={plusBuyImage} alt="Buy" className={styles.icon} />
          </div>
        </div>
      </div>

      {/* Center avatar */}
      <div className={styles.avatarSection} onClick={() => navigate(ROUTES.SETTINGS)}>
        <div className={styles.avatarWrapper}>
          <img
            src={avatar || '/bobe-avatar-placeholder.webp'}
            alt={displayName}
            className={styles.avatar}
          />
        </div>
        <div className={styles.bobeLabel}>
          <span>{displayName}</span>
        </div>
      </div>

      {/* Withdrawal Modals */}
      {/* CRITICAL: DO NOT REMOVE! This modal contains the global wallet connection listener
          that syncs wallet state with backend. Other components (MarketScreen, etc.) 
          depend on this listener being active for wallet connection to work properly. */}
      <WithdrawAmountModal
        visible={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        value={withdrawAmount}
        onChange={setWithdrawAmount}
        onComplete={handleWithdrawComplete}
      />

      <WithdrawSuccessModal
        visible={showSuccessModal}
        onClose={handleCloseSuccess}
        amount={withdrawResult.amount}
        transactionId={withdrawResult.transactionId}
        error={withdrawResult.error}
      />
    </div>
  );
};
